import { facilityAddonIconId, type FacilityIconId } from './assetFacilityIcons';
import {
  catalogRowDisplayName,
  enrichFacilitiesWithCatalog,
  mergeFacilitiesAndPromotedAddonsForDisplay,
} from './assetPromotedFacilities';

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function pushFacilitySource(into: unknown[], raw: unknown): void {
  if (raw == null) {
    return;
  }
  if (Array.isArray(raw)) {
    into.push(...raw);
    return;
  }
  into.push(raw);
}

function collectAssetFacilityRows(asset: Record<string, unknown>): unknown[] {
  const rows: unknown[] = [];
  pushFacilitySource(rows, asset.facilities);
  pushFacilitySource(rows, asset.facility_ids);
  pushFacilitySource(rows, asset.facilityIds);
  if (isPlainObject(asset.facility)) {
    pushFacilitySource(rows, asset.facility.facilities);
    pushFacilitySource(rows, asset.facility.facility_ids);
  }
  if (isPlainObject(asset.asset)) {
    pushFacilitySource(rows, asset.asset.facilities);
    pushFacilitySource(rows, asset.asset.facility_ids);
  }
  return rows;
}

/** True when the listing has every required amenity icon on its facilities/addons. */
export function assetRecordHasAmenityIcons(
  asset: Record<string, unknown>,
  requiredIcons: ReadonlyArray<FacilityIconId>,
  facilityCatalog: Record<string, unknown>[] = [],
): boolean {
  const want = requiredIcons.filter(Boolean);
  if (want.length === 0) {
    return true;
  }
  const merged = mergeFacilitiesAndPromotedAddonsForDisplay(
    collectAssetFacilityRows(asset),
    Array.isArray(asset.addons) ? asset.addons : undefined,
  );
  const enriched =
    facilityCatalog.length > 0 ? enrichFacilitiesWithCatalog(merged, facilityCatalog) : merged;
  const present = new Set<FacilityIconId>();
  for (const row of enriched) {
    const icon = facilityAddonIconId(catalogRowDisplayName(row));
    if (icon) {
      present.add(icon);
    }
  }
  return want.every((icon) => present.has(icon));
}

/** Keep only listings that list every required amenity (e.g. kitchen). */
export function refineAssetsForAmenityIcons(
  assets: Record<string, unknown>[],
  requiredIcons: ReadonlyArray<FacilityIconId> | null | undefined,
  facilityCatalog: Record<string, unknown>[] = [],
): Record<string, unknown>[] {
  const want = (requiredIcons ?? []).filter(Boolean);
  if (want.length === 0) {
    return assets;
  }
  return assets.filter((asset) => assetRecordHasAmenityIcons(asset, want, facilityCatalog));
}
